import React, { useMemo } from 'react';
import { SectionProps } from '../types';
import { DeliveryMode, DeliveryProvider, OrderMethod } from '../../../../types/constants';
import { collect } from 'collect.js';
import { PaymentMethodsEnum, SidebarSectionEnum } from '../enums';
import { CreditCardOutlined, EuroOutlined, TruckOutlined } from '@ant-design/icons';
import OrderMethodIcon from '../../../../assets/icons/OrderMethodIcon';

const LABEL_BY_PAYMENT_METHOD: Record<PaymentMethodsEnum, string> = {
  cash: 'Cash',
  in_store_card: 'In store card',
  online: 'Online',
} as const;

const LABEL_BY_ORDER_METHOD: Partial<Record<OrderMethod, string>> = {
  [OrderMethod.Pickup]: 'Pickup',
  [OrderMethod.Delivery]: 'Delivery',
} as const;

const ICON_BY_PAYMENT_OPTION: Record<PaymentMethodsEnum, typeof TruckOutlined> = {
  [PaymentMethodsEnum.Cash]: EuroOutlined,
  [PaymentMethodsEnum.InStoreCard]: CreditCardOutlined,
  [PaymentMethodsEnum.Online]: CreditCardOutlined,
} as const;

const PaymentMethods = ({ data, ...rest }: SectionProps) => {
  const options = useMemo(
    () =>
      collect(PaymentMethodsEnum)
        .values<PaymentMethodsEnum>()
        .when(!data.payment_settings.has_in_store_card_payments, (collection) =>
          collection.reject((option) => option === PaymentMethodsEnum.InStoreCard),
        )
        .when(!data.payment_settings.has_online_payments, (collection) =>
          collection.reject((option) => option === PaymentMethodsEnum.Online),
        )
        .when(!data.payment_settings.has_cash_payments, (collection) =>
          collection.reject((option) => option === PaymentMethodsEnum.Cash),
        ),
    [
      data.payment_settings.has_in_store_card_payments,
      data.payment_settings.has_cash_payments,
      data.payment_settings.has_online_payments,
    ],
  );
  const getPaymentOptionsByOrderMethod = (orderMethod: OrderMethod) =>
    options
      .when(
        orderMethod === OrderMethod.Delivery &&
          data.order_settings.delivery_mode !== DeliveryMode.Internal,
        (collection) =>
          collection.reject((item) =>
            [PaymentMethodsEnum.Cash, PaymentMethodsEnum.InStoreCard].includes(item),
          ),
      )
      .toArray<PaymentMethodsEnum>();
  const paymentMethods = useMemo(
    () =>
      collect([OrderMethod.Delivery, OrderMethod.Pickup])
        .filter((item) => !!getPaymentOptionsByOrderMethod(item).length)
        .toArray<OrderMethod>(),
    [],
  );

  return (
    <section {...rest} id={SidebarSectionEnum.PaymentMethods}>
      <h4 className="text-md font-semibold text-slate-700">
        <CreditCardOutlined className="mr-2 size-4" />
        Payment methods
      </h4>
      {paymentMethods.map((item) => (
        <article
          key={item}
          className="border-sm border-slate-200 p-3 rounded-lg space-y-2 bg-slate-700/5"
        >
          <h5 className="text-sm font-sm text-slate-700">
            <OrderMethodIcon
              orderMethod={item}
              deliveryProvider={DeliveryProvider.Standalone}
              className="mr-1 size-4"
            />
            {LABEL_BY_ORDER_METHOD[item]}
          </h5>
          {getPaymentOptionsByOrderMethod(item).map((option) => (
            <p key={option} className="text-xs text-slate-500">
              <PaymentOptionIcon option={option} />
              {LABEL_BY_PAYMENT_METHOD[option]}
            </p>
          ))}
        </article>
      ))}
    </section>
  );
};

type PaymentOptionIconProps = {
  option: PaymentMethodsEnum;
};

const PaymentOptionIcon = ({ option }: PaymentOptionIconProps) => {
  const IconComponent = ICON_BY_PAYMENT_OPTION[option];

  return <IconComponent className="mr-1 size-4" />;
};

export default PaymentMethods;
